import React, {Fragment, useCallback} from "react";
import UIIcon from "~/old-ui/UIIcon";
import {createUseStyles} from "react-jss";
import {brandColor, defaultFontSize, primaryTextColor, secondaryTextColor, smallFontSize} from "~/theme";
import {getEventHint} from "~/types/event";
import {useLazyQuery} from "@apollo/client";
import gql from "graphql-tag";
import {externalUrl} from "~/utils";
import {Popover, Transition} from '@headlessui/react';
import cn from "classnames";

export default function NavbarNewEventMenuItem({accountId}) {
  const classes = useStyles();

  const [_fetchRecentEvents, {
    data: recentEventsData,
    called: fetchRecentEventsCalled
  }] = useLazyQuery(getRecentEventsQuery, {
    variables: {accountId: accountId}
  });

  const onDropDownFocus = useCallback(() => {
    if (!fetchRecentEventsCalled) {
      _fetchRecentEvents();
    }
  }, [_fetchRecentEvents, fetchRecentEventsCalled]);


  return (
    <>
      <Popover className="tailwind relative">
        <>
          <Popover.Button className={classes.root}
                          onFocus={onDropDownFocus}
                          onMouseOver={onDropDownFocus}
                          onClick={() => {
                            _fetchRecentEvents();
                          }}>
            <UIIcon name='fa-calendar-plus' className={classes.icon}/>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className="absolute z-10 w-80 py-4 mt-4 transform -translate-x-1/2 left-1
                bg-white rounded-md shadow-lg ring-1 ring-gray ring-opacity-20 overflow-hidden ">
              <h3 className={cn(classes.duplicateEventTitle, 'px-4')}>Duplicate event</h3>

              <div className={classes.eventList}>
                {recentEventsData ? recentEventsData.events.map((event) => (
                  <a key={event.id} href={externalUrl(`/event/${event.id}/duplicate`)}
                     className={cn(classes.event, 'mb-2 px-4 py-1')}>
                    <UIIcon
                      name='fa-copy-light'
                      className={classes.eventIcon}
                    />
                    <div className={classes.eventInfo}>
                      <h4 className={classes.eventName}>{event.title}</h4>
                      <span className={classes.eventDescription}>{getEventHint(event)}</span>
                    </div>
                  </a>
                )) : (
                  <span className={classes.loadingIndicator}>Loading...</span>
                )}
              </div>

              <hr className={classes.hr}/>

              <a className={classes.addLink} href={externalUrl('/events/new/')}>Add event</a>
            </Popover.Panel>
          </Transition>
        </>
      </Popover>
    </>
  );
}

const getRecentEventsQuery = gql`
  query NavbarGetRecentEvents($accountId: ID!) {
    events: recentEvents(accountId: $accountId, count: 5) {
      id
      title
      location {
        city
        country
        online
      }
      schedule {
        start
        end
      }
    }
  }
`;

const useStyles = createUseStyles({
  root: {
    width: 25,
    height: 25,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  icon: {
    color: '#fff',
    width: 16,
    height: 16,
    cursor: 'pointer',
    outline: 'none',
  },

  dropdown: {
    width: 320,
    marginTop: 8,
    padding: [[16, 0]]
  },

  duplicateEventTitle: {
    color: secondaryTextColor,
    margin: [[0, 0, 16]],
    fontSize: smallFontSize,
    fontWeight: 400,
    textTransform: 'uppercase',
  },

  event: {
    display: 'flex',
    width: '100%',
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: '#eee',
    }
  },

  eventIcon: {
    minWidth: 28,
    width: 28,
    height: 28,
    marginTop: 4,
  },

  eventInfo: {
    boxSizing: 'content-box',
    paddingLeft: 8,
    minWidth: 0,
  },

  eventName: {
    margin: [[0, 0, 4]],
    padding: 0,
    fontSize: defaultFontSize,
    fontWeight: 400,
    color: primaryTextColor,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  eventDescription: {
    display: 'block',
    fontSize: defaultFontSize,
    color: secondaryTextColor,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  hr: {
    border: 0,
    borderBottom: [[1, 'solid', '#E7E7E7']],
    margin: [[20, 0]],
  },

  addLink: {
    display: 'block',
    color: brandColor,
    textDecoration: [['none', '!important']],
    transition: [['all', '0.15s', 'ease']],
    fontSize: defaultFontSize,
    margin: [[0, 16]],
  },

  loadingIndicator: {
    margin: [[8, 16, 8, 18]],
    textAlign: 'center',
    color: secondaryTextColor,
    fontSize: defaultFontSize,
  }
});
